import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { push } from 'react-router-redux'
import axios from 'axios'
import { Map } from 'immutable'
import { getAccessToken } from '@driftt/web-auth'
import { pathOr } from 'ramda'

import * as UserSelectors from 'app/modules/user/selectors'
import * as OrgSelectors from 'app/modules/org/selectors'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import app from 'containers/app'
import ACCESS from 'utils/Access'
import { captureResponseException } from 'utils/SentryHelpers'

import { api } from './api'
import * as actions from './actions'
import * as selectors from './selectors'
import * as constants from './constants'
import * as ConversationActions from 'app/modules/conversation/actions'
import { METRICS_API_BASE } from 'constants/ResourcesConstants'

const fetchPlaybook = action$ => {
  return action$
    .ofType(actions.fetchPlaybook.toString())
    .pluck('payload')
    .mergeMap(({ id }) => {
      return api
        .fetch(id)
        .map(actions.receivePlaybookDraft)
        .startWith(actions.fetchPlaybookPending({ id }))
        .catch(error => {
          return Observable.of(actions.fetchPlaybookFailed({ id, error }))
        })
    })
}

const getPlaybook = action$ =>
  action$
    .ofType(actions.getPlaybook.toString())
    .pluck('payload')
    .mergeMap(({ playbookId, nextAction }) =>
      api
        .fetch(playbookId)
        .flatMap(playbook => [nextAction(playbook), actions.getPlaybookSuccess(playbook)])
        .startWith(actions.getPlaybookPending({ playbookId }))
        .catch(error => Observable.of(actions.getPlaybookFailed({ playbookId, error })))
    )

const allChanges = action$ => {
  return action$
    .ofType(
      actions.setActionInboxId.toString(),
      actions.setName.toString(),
      actions.setSlug.toString(),
      actions.setGreeting.toString(),
      actions.addQuestion.toString(),
      actions.addEmailCaptureQuestion.toString(),
      actions.addPhoneCaptureQuestion.toString(),
      actions.deleteQuestion.toString(),
      actions.moveQuestionUp.toString(),
      actions.addAnswer.toString(),
      actions.deleteAnswer.toString(),
      actions.flagAnswer.toString(),
      actions.setSkipTo.toString(),
      actions.setFieldAttributeName.toString(),
      actions.setQuestionPrompt.toString(),
      actions.setSenderName.toString(),
      actions.setMessage.toString(),
      actions.setVideoUrl.toString(),
      actions.setEmailMessage.toString(),
      actions.setEmailOverride.toString(),
      actions.setEmailTitle.toString(),
      actions.setEmailDelay.toString(),
      actions.setSequenceEmailOverride.toString(),
      actions.setSequenceSenderName.toString(),
      actions.setSequenceAuthorId.toString(),
      actions.setHeadline.toString(),
      actions.setThankYouMessage.toString(),
      actions.setThankYouHeadline.toString(),
      actions.setCtaType.toString(),
      actions.setCtaLink.toString(),
      actions.setCtaCopy.toString(),
      actions.setMetaAttribute.toString(),
      actions.setAuthorId.toString(),
      actions.setEmailTheme.toString(),
      actions.addGoal.toString(),
      actions.deleteGoal.toString(),
      actions.setGoalClassification.toString(),
      actions.setGoalMessage.toString(),
      actions.deleteKeyword.toString(),
      actions.addKeyword.toString(),
      actions.setKeywordText.toString(),
      actions.setKeywordType.toString(),
      actions.setAnswerType.toString(),
      actions.setButtonText.toString(),
      actions.setResponse.toString(),
      actions.setDefaultPhoneCountryCode.toString(),
      actions.saveGoalAction.toString(),
      actions.removeGoalAction.toString(),
      actions.saveGoalRoutingOption.toString(),
      actions.setDraftFrequency.toString(),
      actions.setAudience.toString(),
      actions.setUserListIds.toString(),
      actions.setExcludedUserListIds.toString(),
      actions.setConditionValue.toString(),
      actions.setConditionField.toString(),
      actions.setConditionOperator.toString(),
      actions.addCondition.toString(),
      actions.addConditionSet.toString(),
      actions.deleteCondition.toString(),
      actions.deleteConditionSet.toString(),
      actions.setEmailTitle.toString(),
      actions.setEmailDelay.toString(),
      actions.setWeekdaysOnly.toString(),
      actions.setSmartSending.toString(),
      actions.setUnenrollOnReply.toString(),
      actions.setUnenrollOnReplyByDomain.toString(),
      actions.setThreadedWithPrevious.toString(),
      actions.addEmail.toString(),
      actions.deleteEmail.toString(),
      actions.moveEmailUp.toString(),
      actions.setSmartFilterEngagedContacts.toString(),
      actions.setMarketingEmailLayout.toString(),
      actions.setEmailBannerSuccess.toString(),
      actions.clearEmailBanner.toString(),
      actions.setEmailPlaybookConditionValue.toString(),
      actions.setButtonOnlyResponses.toString(),
      actions.setSequenceIndividualEmailOverride.toString(),
      actions.setSequenceIndividualSenderName.toString(),
      actions.setSequenceIndividualDynamicAttr.toString(),
      actions.setSequenceIndividualDynamicFormat.toString(),
      actions.setSignatureBody.toString(),
      actions.setSignatureIncludeAvatar.toString(),
      actions.setSignatureIncludeSocial.toString(),
      actions.setSignatureIncludeBanner.toString(),
      actions.setSignatureBannerSuccess.toString(),
      actions.setSignatureBannerLinkURL.toString(),
      actions.clearSignatureBanner.toString(),
      actions.removeSignature.toString(),
      actions.addEmptyBannerSignature.toString(),
      actions.removeEmptyBannerSignature.toString(),
    )
    .filter(action => (action.payload && !action.payload.dontSave) || !action.payload)
    .map(actions.allChanges)
}

const disablePlaybook = (action$, store) => {
  return action$.ofType(actions.allChanges.toString()).switchMap(() => {
    const autosaveDisabled = store.getState().getIn(['playbooks', 'ui', 'autosave']) === false
    return Observable.of(autosaveDisabled ? actions.markDraftUnsaved() : actions.savePlaybookDraft())
  })
}

const getPlaybookByCampaignId = action$ =>
  action$
    .ofType(actions.getPlaybookByCampaignId.toString())
    .pluck('payload')
    .mergeMap(({ campaignId }) =>
      api
        .fetchPlaybookByCampaignId(campaignId)
        .flatMap(playbook => {
          return [
            actions.searchPlaybooksSuccess(playbook),
            actions.getPlaybookByCampaignIdSuccess({ campaignId, playbook: playbook }),
          ]
        })
        .catch(err => Observable.of(actions.getPlaybookByCampaignIdFailed({ campaignId, err })))
    )

const fetchPlaybookByCampaignId = action$ => {
  return action$
    .ofType(actions.fetchPlaybookByCampaignId.toString())
    .pluck('payload')
    .mergeMap(({ campaignId, conversationId }) => {
      return api
        .fetchPlaybookByCampaignId(campaignId)
        .flatMap(playbook => {
          return [
            actions.searchPlaybooksSuccess(playbook),
            ConversationActions.setPlaybookIdForConversation({
              conversationId,
              playbookId: playbook.get('result').get(0),
            }),
          ]
        })
        .startWith(actions.fetchPlaybookByCampaignIdPending({ conversationId }))
        .catch(error => {
          return Observable.of(actions.fetchPlaybookByCampaignIdFailed({ conversationId, error }))
        })
    })
}

const fetchPlaybookCardinality = action$ => {
  return action$
    .ofType(actions.fetchPlaybookCardinality.toString())
    .debounceTime(300)
    .pluck('payload')
    .switchMap(userListObj => {
      // we might be passed empty userListIds, in which case, we want to return 0
      // it's a little silly to expect the API to check empty lists, as checking query params like that
      // is at the mercy of Dropwizard's API
      const { userListIds, excludedUserListIds, smartFilters, isEmailPlaybook } = userListObj
      const isEmpty = maybeArrayMaybeList =>
        maybeArrayMaybeList.size === 0 || maybeArrayMaybeList.length === 0
      const isObjEmpty = obj => Map(obj).reduce((hasBeenNull, value) => hasBeenNull && value === null, true)
      if (
        !!userListIds &&
        !!excludedUserListIds &&
        (isEmpty(userListIds) && isEmpty(excludedUserListIds) && isObjEmpty(smartFilters))
      ) {
        return Observable.of(actions.fetchPlaybookCardinalitySuccess({ cardinality: 0 }))
      }
      return api
        .fetchCardinality(userListObj)
        .flatMap(res => Observable.of(actions.fetchPlaybookCardinalitySuccess(res.response)))
        .startWith(actions.fetchPlaybookCardinalityPending())
        .catch(error => Observable.of(actions.fetchPlaybookCardinalityFailed({ error })))
    })
}

const fetchPlaybookByInteractionId = action$ => {
  return action$
    .ofType(actions.fetchPlaybookByInteractionId.toString())
    .pluck('payload')
    .mergeMap(({ interactionId, conversationId }) => {
      return api
        .fetchPlaybookByInteractionId(interactionId)
        .flatMap(playbook => {
          return [
            actions.searchPlaybooksSuccess(playbook),
            ConversationActions.setPlaybookIdForConversation({
              conversationId,
              playbookId: playbook.get('result').get(0),
            }),
          ]
        })
        .startWith(actions.fetchPlaybookByInteractionIdPending({ conversationId }))
        .catch(error => {
          return Observable.of(actions.fetchPlaybookByInteractionIdFailed({ conversationId, error }))
        })
    })
}

const fetchPlaybooksByIds = action$ => {
  return action$
    .ofType(actions.fetchPlaybooksByIds.toString())
    .pluck('payload')
    .mergeMap(playbookIds => {
      return api
        .byIds(playbookIds)
        .map(({ playbooks }) => {
          return actions.fetchPlaybooksByIdsSuccess(playbooks)
        })
        .startWith(actions.fetchPlaybooksByIdsPending({ playbookIds }))
        .catch(error => {
          return Observable.of(actions.fetchPlaybooksByIdsFailed({ playbookIds, error }))
        })
    })
}

const createPlaybook = action$ => {
  return action$
    .ofType(actions.createPlaybook.toString())
    .pluck('payload')
    .mergeMap(type => {
      return api
        .create(type)
        .mergeMap(draft => {
          return Observable.merge(
            Observable.of(actions.receivePlaybookDraft(draft)),
            Observable.of(push(`/playbooks/${draft.get('result')}/edit`))
          )
        })
        .startWith(actions.createPlaybookPending())
        .catch(error => {
          return Observable.of(actions.createPlaybookFailed({ error }))
        })
    })
}

const clonePlaybook = action$ => {
  return action$
    .ofType(actions.clonePlaybook.toString())
    .pluck('payload')
    .mergeMap(id => {
      return api
        .clone(id)
        .mergeMap(draft => {
          return Observable.merge(
            Observable.of(actions.receivePlaybookDraft(draft)),
            Observable.of(push(`/playbooks/${draft.get('result')}/edit`))
          )
        })
        .startWith(actions.clonePlaybookPending(id))
        .catch(error => {
          return Observable.of(actions.clonePlaybookFailed({ error, id }))
        })
    })
}

const sendTestEmail = (action$, store) => {
  return action$
    .ofType(actions.sendTestEmail.toString())
    .pluck('payload')
    .mergeMap(({ emailId }) => {
      const state = store.getState()
      const actionId = selectors.getFirstActionType(state).get('id')
      const playbookId = selectors.draftId(state)
      const orgId = OrgSelectors.orgId(state)
      const playbook = selectors.denormalizedDraft(state)
      const email = UserSelectors.currentUser(state).email
      playbook.orgId = orgId
      return api
        .sendTestEmail(playbookId, actionId, email, emailId, playbook)
        .map(() => {
          app.actions.NotificationActions.pushNotification({
            content: `Test email sent to ${email}.`,
            variant: 'green',
          })
          return actions.sendTestEmailSuccess()
        })
        .startWith(actions.sendTestEmailPending())
        .catch(error => {
          if (error.response.message.indexOf('authenticated') > -1) {
            app.actions.NotificationActions.pushNotification({
              content: "You haven't authenticated your gmail account yet so we can't send test emails!",
              variant: 'red',
              sticky: true,
            })
          } else {
            app.actions.NotificationActions.pushNotification({
              content: 'There was an error sending your test email, please try again.',
              variant: 'red',
            })
          }
          return Observable.of(actions.sendTestEmailFailed(error))
        })
    })
}

const sendTestSequence = (action$, store) => {
  return action$
    .ofType(actions.sendTestSequence.toString())
    .pluck('payload')
    .mergeMap(() => {
      const state = store.getState()
      const actionId = selectors.getFirstActionType(state).get('id')
      const playbookId = selectors.draftId(state)
      const orgId = OrgSelectors.orgId(state)
      const playbook = selectors.denormalizedDraft(state)
      const email = UserSelectors.currentUser(state).email
      playbook.orgId = orgId
      return api
        .sendTestSequence(playbookId, actionId, email, playbook)
        .map(() => {
          app.actions.NotificationActions.pushNotification({
            content: `Test sequence sent to ${email}, your emails will arrive about 5 minutes apart.`,
            variant: 'green',
          })
          return actions.sendTestSequenceSuccess()
        })
        .startWith(actions.sendTestSequencePending())
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: 'There was an error sending your test sequence, please try again.',
            variant: 'red',
          })
          return Observable.of(actions.sendTestSequenceFailed(error))
        })
    })
}

const fetchPlaybooksPriority = action$ => {
  return action$
    .ofType(actions.fetchPlaybooksPriority.toString())
    .pluck('payload')
    .switchMap(() => {
      return api
        .search([constants.PlaybookStatus.ENABLED, constants.PlaybookStatus.DISABLED], false) // don't normalize
        .map(actions.fetchPlaybooksPrioritySuccess)
        .startWith(actions.fetchPlaybooksPriorityPending())
        .catch(error => {
          return Observable.of(actions.fetchPlaybooksPriorityFailed({ error }))
        })
    })
}

const fetchChatActivePlaybooks = action$ => (
  action$
    .ofType(actions.fetchChatActivePlaybooks.toString())
    .mergeMap(() => (
      api
        .fetchChatActive()
        .map(actions.searchPlaybooksSuccess)
        .startWith(actions.searchPlaybooksPending())
        .catch(err => Observable.of(actions.searchPlaybooksFailed({ err })))
    ))
)

const updateStatus = action$ => {
  return action$
    .ofType(actions.setStatus.toString())
    .pluck('payload')
    .mergeMap(({ id, status }) => {
      return api
        .updateStatus(id, status)
        .flatMap(() => {
          return [
            actions.savePlaybookSuccess(),
            actions.fetchPlaybookCounts(),
            actions.fetchPlaybooksPriority(),
          ]
        })
        .startWith(actions.savePlaybookPending({ id }))
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: "We were unable to save your playbook, please contact support for more information",
            variant: 'red',
          })
          return Observable.of(actions.savePlaybookFailed({ id, error }))
        })
    })
}

const updateDraftStatus = (action$, { getState }) => {
  return action$
    .ofType(actions.setDraftStatus.toString())
    .pluck('payload')
    .mergeMap(({ status }) => {
      const playbook = selectors.denormalizedDraft(getState())
      return api
        .updateStatus(playbook.id, status)
        .map(() => actions.setDraftStatusSuccess({ status }))
        .startWith(actions.setDraftStatusPending())
        .catch((err) => {
          let errorMessage

          const responseText = pathOr('', ['response', 'message'], err)
          // cases for restricted functionality on sunset email features
          switch (responseText) {
            case 'org_cannot_enable_sales_sequence_playbooks':
            case 'org_cannot_enable_marketing_email_playbooks':
              errorMessage = 'This playbook type can no longer be enabled'
              break
              
            case 'org_cannot_clone_sales_sequence_playbooks':
            case 'org_cannot_clone_marketing_email_playbooks':
              errorMessage = 'Cloning has been disabled for this playbook type'
              break

            default:
              errorMessage = 'Could not save changes'
              break
          }

          app.actions.NotificationActions.pushNotification({
            content: errorMessage,
            variant: 'red',
            sticky: true,
          })
          
          return Observable.merge(
            Observable.of(actions.setDraftStatusFailed()),
            Observable.of(actions.setDraftStatus({ status: constants.PlaybookStatus.DISABLED })),
          )
        })
    })
}

const setDraftSuccess = (action$, { getState }) => {
  return action$
    .ofType(actions.setDraftStatusSuccess.toString())
    .pluck('payload')
    .mergeMap(({ status }) => {
      if (selectors.getIsEmailPlaybook(getState()) && (status === 'ENABLED' || status === 'SENT')) {
        const appNotif = status === 'ENABLED'
          ? (selectors.getIsOutboundSequencePlaybook(getState()) ?
            'Your email was successfully enabled!' :
              'Your email was successfully sent and enabled!')
            : 'Your email was successfully sent!'
        app.actions.NotificationActions.pushNotification({
          content: appNotif,
        })
      }
      return Observable.empty()
    })
}

const saveDraft = (action$, { getState }) => {
  return action$
    .ofType(actions.savePlaybookDraft.toString(), actions.disablePlaybook.toString())
    .debounceTime(1000)
    .mergeMap(() => {
      const playbook = selectors.denormalizedDraft(getState())
      if (playbook.meta.driftLinkOnly) {
        playbook.targeting.display = []
        playbook.targeting.userListIds = []
        playbook.targeting.excludedUserListIds = []
        playbook.targeting.audience = constants.AudienceType.NONE
      }
      return api
        .update(playbook)
        .map(() => actions.searchPlaybooks([playbook.status]))
        .mergeMap(() => Observable.of(actions.receivePlaybookDraft()))
        .startWith(actions.savePlaybookDraftPending({ id: playbook.id }))
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            content: "We were unable to save your playbook, please contact support for more information",
            variant: 'red',
          })
          return Observable.of(actions.savePlaybookDraftFailed({ id: playbook.id, error }))
        })
    })
}

const scheduleDraft = (action$, { getState }) => {
  return action$.ofType(actions.schedulePlaybookDraft.toString()).mergeMap(() => {
    const playbook = selectors.denormalizedDraft(getState())
    playbook.status = constants.PlaybookStatus.SCHEDULED
    return api
      .update(playbook)
      .mergeMap(draft => {
        return Observable.merge(
          Observable.of(actions.receivePlaybookDraft(draft)),
          Observable.of(push(`/playbooks`))
        )
      })
      .startWith(actions.savePlaybookDraftPending({ id: playbook.id }))
      .catch(error => {
        app.actions.NotificationActions.pushNotification({
          content: "We were unable to save your playbook, please contact support for more information",
          variant: 'red',
        })
        return Observable.of(actions.savePlaybookDraftFailed({ id: playbook.id, error }))
      })
  })
}

const receivePlaybook = (action$, { getState }) => {
  return action$
    .ofType(actions.receivePlaybookDraft.toString())
    .pluck('payload')
    .mergeMap(response => {
      const normalizedLocal = selectors.draftRoot(getState())
      if (normalizedLocal.size === 0) {
        return Observable.of(actions.acceptPlaybookDraft(response))
      } else {
        return Observable.empty()
      }
    })
}

const fetchPlaybooksCounts = action$ => {
  return action$.ofType(actions.fetchPlaybookCounts.toString()).mergeMap(() => {
    return api
      .counts()
      .map(actions.fetchPlaybookCountsSuccess)
      .startWith(actions.fetchPlaybookCountsPending)
      .catch(error => {
        return Observable.of(actions.fetchPlaybookCountsFailed({ error }))
      })
  })
}

const searchPlaybooks = action$ => {
  return action$
    .ofType(actions.searchPlaybooks.toString())
    .pluck('payload')
    .mergeMap(status => {
      return api
        .search(status)
        .map(actions.searchPlaybooksSuccess)
        .startWith(actions.searchPlaybooksPending())
        .catch(error => {
          return Observable.of(actions.searchPlaybooksFailed({ error }))
        })
    })
}

const searchEnabledPlaybooks = action$ => {
  return action$.ofType(actions.searchEnabledPlaybooks.toString()).mergeMap(() => {
    return api
      .fetchEnabled()
      .map(actions.searchPlaybooksSuccess)
      .startWith(actions.searchPlaybooksPending())
      .catch(error => {
        return Observable.of(actions.searchPlaybooksFailed({ error }))
      })
  })
}

const setCurrentGoalId = (action$, { getState }) => {
  return action$.ofType(actions.setCurrentGoalId.toString()).mergeMap(() => {
    const currentGoal = selectors.currentGoal(getState())
    return Observable.of(actions.selectGoalRoutingOptionUI(currentGoal.routing))
  })
}

const fetchDataPreviewEmail = (action$, { getState }) => {
  return action$
    .ofType(actions.fetchDataPreviewEmail.toString())
    .pluck('payload')
    .mergeMap(({ playbookId, userListIds, campaignId }) => {
      return (
        api
          // TODO: _.unescape is a very naive un-escaping function. We definitely want something more robust, since the API
          // endpoint will break on things like the single quote hex code instead of the normal HTMl code. e.g.: &#39; -> ✅ ; &#x26; -> ❌
          .fetchDataPreviewEmail(
            _.unescape(selectors.emailMessage(getState()).message),
            userListIds,
            campaignId
          )
          .map(templateResponse =>
            actions.fetchDataPreviewEmailSuccess({
              template: templateResponse.template,
              playbookId,
            })
          )
          .startWith(actions.fetchDataPreviewEmailPending())
          .catch(error => {
            return Observable.of(actions.fetchDataPreviewEmailFailed({ error }))
          })
      )
    })
}

const fetchDataPreviewSequenceEmail = (action$, { getState }) => {
  return action$
    .ofType(actions.fetchDataPreviewSequenceEmail.toString())
    .pluck('payload')
    .mergeMap(({ emailId, userListIds, campaignId, userId }) => {
      return (
        api
          // TODO: _.unescape is a very naive un-escaping function. We definitely want something more robust, since the API
          // endpoint will break on things like the single quote hex code instead of the normal HTMl code. e.g.: &#39; -> ✅ ; &#x26; -> ❌
          .fetchDataPreviewEmail(
            _.unescape(selectors.getEmail(getState(), { id: emailId }).message),
            userListIds,
            campaignId,
            userId
          )
          .map(templateResponse =>
            actions.fetchDataPreviewSequenceEmailSuccess({
              template: templateResponse.template,
              emailId,
            })
          )
          .startWith(actions.fetchDataPreviewSequenceEmailPending())
          .catch(error => {
            return Observable.of(actions.fetchDataPreviewSequenceEmailFailed({ error }))
          })
      )
    })
}

const enroll = action$ => {
  return action$
    .ofType(actions.enroll.toString())
    .pluck('payload')
    .mergeMap(({ action, playbookId, email }) => {
      return api
        .enroll(playbookId, email, action)
        .map(() => actions.enrollSuccess({ email }))
        .startWith(actions.enrollPending({ email }))
        .catch(request => {
          return Observable.of(
            actions.enrollFailed({
              email,
              error: request.response ? request.response.message : 'Something went wrong',
            })
          )
        })
    })
}

const unenroll = action$ => {
  return action$
    .ofType(actions.unenroll.toString())
    .pluck('payload')
    .mergeMap(({ endUserId }) => {
      return api
        .unenroll(endUserId)
        .map(() => {
          app.actions.NotificationActions.pushNotification({
            content: 'You unenrolled this user from all sequences.',
          })
          return actions.unenrollSuccess()
        })
        .startWith(actions.unenrollPending())
        .catch(error => {
          return Observable.of(actions.unenrollFailed(error))
        })
    })
}

const fetchEnrolledPlaybookId = action$ => {
  return action$
    .ofType(actions.fetchEnrolledPlaybookId.toString())
    .pluck('payload')
    .switchMap(({ email }) => {
      return api
        .fetchEnrolledPlaybookId(email)
        .map(data => {
          return actions.fetchEnrolledPlaybookIdSuccess(!data.response.lockTTL && data.response.playbookId)
        })
        .startWith(actions.fetchEnrolledPlaybookIdPending())
        .catch(error => {
          if (error.status) {
            return Observable.of(actions.fetchEnrolledPlaybookIdSuccess(false))
          }
          return Observable.of(actions.fetchEnrolledPlaybookIdFailed())
        })
    })
}

const notify = action$ => {
  return action$
    .ofType(actions.notify.toString())
    .pluck('payload')
    .mergeMap(({ playbookId, userIds }) => {
      return api
        .notify(playbookId, userIds)
        .map(() => {
          app.actions.NotificationActions.pushNotification({
            content: 'You successfully shared this sequence.',
          })
          return actions.notifySuccess()
        })
        .startWith(actions.notifyPending())
        .catch(error => Observable.of(actions.notifyFailed(error)))
    })
}

const fetchEnrolledSequenceCount = (action$, { getState }) => {
  return action$.ofType(actions.fetchEnrolledSequenceCount.toString()).mergeMap(() => {
    const state = getState()
    const authorId = UserSelectors.currentUser(state).id
    const hasSalesSequences = ACCESS.hasScopes('SALES_SEQUENCES')
    return api
      .fetchEnrolledSequenceCount({ authorId, hasSalesSequences })
      .map(actions.fetchEnrolledSequenceCountSuccess)
      .startWith(actions.fetchEnrolledSequenceCountPending())
      .catch(() => Observable.of(actions.fetchEnrolledSequenceCountFailed()))
  })
}

const fetchPlaybookForEndUser = action$ => {
  return action$
    .ofType(actions.fetchPlaybookForEndUser.toString())
    .pluck('payload')
    .mergeMap(({ email, playbookId }) => {
      return api
        .fetchPlaybookForEndUser({ playbookId, email })
        .map(actions.fetchPlaybookForEndUserSuccess)
        .startWith(actions.fetchPlaybookForEndUserPending())
        .catch(() => Observable.of(actions.fetchPlaybookForEndUserFailed()))
    })
}

const uploadEmailBannerImage = action$ =>
  action$
    .ofType(actions.setEmailBanner.toString())
    .pluck('payload')
    .mergeMap(({ emailId, file }) =>
      api
        .uploadImage(file)
        .map(res => actions.setEmailBannerSuccess({ res, emailId }))
        .startWith(actions.setEmailBannerPending({ emailId }))
        .catch(() => Observable.of(actions.setEmailBannerFailed({ emailId })))
    )

const uploadSignatureBannerImage = action$ =>
  action$
    .ofType(actions.setSignatureBanner.toString())
    .pluck('payload')
    .mergeMap(({ file, signatureId }) =>
      api
        .uploadImage(file)
        .map(res => actions.setSignatureBannerSuccess({ value: res.url, signatureId }))
        .startWith(actions.setSignatureBannerPending())
        .catch((err) => Observable.of(actions.setSignatureBannerFailed({ err })))
    )

const fetchPlaybooksByGatedPlan = action$ =>
  action$.ofType(actions.fetchPlaybooksByGatedPlan.toString()).mergeMap(() =>
    api
      .fetchPlaybooksByGatedPlan()
      .map(actions.fetchPlaybooksByGatedPlanSuccess)
      .startWith(actions.fetchPlaybooksByGatedPlanPending())
      .catch(err => Observable.of(actions.fetchPlaybooksByGatedPlanFailed({ err })))
  )


const fetchSignatureMultiBannerPreview = (action$, store) =>
  action$
    .ofType(actions.fetchSignatureMultiBannerPreview.toString())
    .pluck('payload')
    .debounceTime(350) // average keystroke frequency
    .switchMap(({ userId, isPlaybookEditor = false }) => {
      const state = store.getState()
      const orgId = OrgSelectors.orgId(state)
      const playbook = selectors.denormalizedDraft(state)
      playbook.orgId = orgId
      return api
        .fetchSignatureMultiBannerPreviewForPlaybook({ playbook, userId, isPlaybookEditor })
        .map(actions.fetchSignatureMultiBannerPreviewSuccess)
        .startWith(actions.fetchSignatureMultiBannerPreviewPending())
        .catch(err => Observable.of(actions.fetchSignatureMultiBannerPreviewFailed({ err })))
    })

const asyncButtonPress = action$ =>
  action$
    .ofType(actions.asyncButtonPress.toString())
    .mergeMap(
      ({
        payload: {
          url,
          successMessage,
          errorMessage,
          data = null,
          clickId,
          method = 'GET',
          successCallback = () => {},
          errorCallback = () => {},
        },
      }) => {
        return Observable.fromPromise(
          axios({
            method: method.toLowerCase(),
            data,
            url,
            headers: {
              'content-type': 'application/json; charset=UTF-8',
              'Authorization': `bearer ${getAccessToken()}`
            },
          })
        )
          .map(res => {
            app.actions.NotificationActions.pushNotification({
              content: successMessage,
              variant: 'green',
            })
            successCallback.call(null, res)
            return actions.asyncButtonPressSuccess({ clickId, meta: { at: Date.now() }})
          })
          .startWith(actions.asyncButtonPressPending({ clickId, meta: { at: Date.now() }}))
          .catch(err => {
            app.actions.NotificationActions.pushNotification({
              content: errorMessage,
              variant: 'red',
            })
            errorCallback.call(null, err)
            return Observable.of(actions.asyncButtonPressFailed({ clickId, meta: { err, at: Date.now() }}))
          })
      }
    )

const createInitialWelcomePlaybooks = action$ =>
  action$
    .ofType(actions.createInitialWelcomePlaybooks.toString())
    .pluck('payload')
    .switchMap(({ onlineMsg, offlineMsg }) => (
      api
        .createInitialWelcomePlaybooks({ onlineMsg, offlineMsg })
        .map(actions.createInitialWelcomePlaybooksSuccess)
        .startWith(actions.createInitialWelcomePlaybooksPending())
        .catch((err) => Observable.of(actions.createInitialWelcomePlaybooksFailed(err)))
    ))

const fetchPlaybookForCampaignId = (action$, store) =>
  action$
    .ofType(actions.fetchPlaybookForCampaignId.toString())
    .pluck('payload')
    .filter(({ campaignId }) => PlaybookSelectors.playbookForCampaignIdNeverFetched(store.getState(), campaignId))
    .mergeMap(({ campaignId }) =>
      api
        .fetchPlaybookByCampaignIdUntouched(campaignId)
        .map(playbook => actions.fetchPlaybookForCampaignIdSuccess({ data: playbook, campaignId }))
        .startWith(actions.fetchPlaybookForCampaignIdPending({ campaignId }))
        .catch(err => actions.fetchPlaybookForCampaignIdFailed({ campaignId, err }))
    )

const fetchPlaybooksByReportType = action$ =>
    action$
      .ofType(actions.fetchPlaybooksByReportType.toString())
      .pluck('payload')
      .mergeMap(reportType => {
        return api
          .fetchPlaybooksByReportType({ reportType })
          .map(playbooks => actions.fetchPlaybooksByReportTypeSuccess({ reportType, playbooks }))
          .startWith(actions.fetchPlaybooksByReportTypePending())
          .catch(() => Observable.of(actions.fetchPlaybooksByReportTypeFailed()))
      })

const enableAllPlaybooksByReportType = action$ =>
    action$
      .ofType(actions.enableAllPlaybooksByReportType.toString())
      .pluck('payload')
      .mergeMap(({ orgId, reportType }) => {
        return api
          .enableAllPlaybooksByReportType({ orgId, reportType })
          .map(actions.enableAllPlaybooksByReportTypeSuccess)
          .startWith(actions.enableAllPlaybooksByReportTypePending())
          .catch(err => Observable.of(actions.enableAllPlaybooksByReportTypeFailed(err)))
      })

const recordPlaybookReportLoadTime = action$ =>
  action$
      .ofType(actions.startReportTimer.toString())
      .pluck('payload')
      .mergeMap(({ playbookId }) => {
        const start = Date.now()
        let count = 0
        let startedIncrementing = false

        const increment$ = action$
          .ofType(actions.incrementReportTimer.toString())
          .pluck('payload.playbookId')
          .filter(localPlaybookId => playbookId !== localPlaybookId)
          .map(() => ({ count: 1 }))

        const decrement$ = action$
          .ofType(actions.decrementReportTimer.toString())
          .pluck('payload.playbookId')
          .filter(localPlaybookId => playbookId !== localPlaybookId)
          .map(() => ({ count: -1 }))

        const stopTaking$ = Observable.merge(
          action$
            .ofType(actions.decrementReportTimer.toString(), actions.incrementReportTimer.toString())
            .pluck('payload.playbookId')
            .filter(localPlaybookId => playbookId !== localPlaybookId)
            .filter(() => count === 0 && startedIncrementing),
          Observable.timer(5 * 60 * 1000) // 5min timeout
        )

        return Observable.merge(increment$, decrement$)
          .mergeMap(({ count: countChange }) => {
            startedIncrementing = true
            count += countChange
            if (count === 0) {
              const elapsed = Date.now() - start
              return Observable.fromPromise(
                axios({
                  method: 'POST',
                  data: {
                    metric: 'playbook.report.pageLoad',
                    dimensions: {
                      type: 'timer',
                    },
                    value: elapsed,
                  },
                  url: `${METRICS_API_BASE}/monitoring/metrics/add`,
                  headers: {
                    'content-type': 'application/json; charset=UTF-8',
                    'Authorization': `bearer ${getAccessToken()}`
                  },
                })
              )
              .map(actions.recordedReportTimer)
              .catch(() => Observable.of(actions.recordedReportTimer()))
            } else {
              return Observable.empty()
            }
          })
          .takeUntil(stopTaking$)
      })

export const epic = combineEpics(
  fetchPlaybook,
  getPlaybook,
  fetchPlaybooksCounts,
  saveDraft,
  scheduleDraft,
  updateStatus,
  updateDraftStatus,
  createPlaybook,
  searchPlaybooks,
  searchEnabledPlaybooks,
  receivePlaybook,
  clonePlaybook,
  fetchPlaybooksPriority,
  setCurrentGoalId,
  sendTestEmail,
  sendTestSequence,
  disablePlaybook,
  allChanges,
  fetchPlaybooksByIds,
  fetchPlaybookByCampaignId,
  fetchPlaybookByInteractionId,
  fetchDataPreviewEmail,
  enroll,
  unenroll,
  fetchPlaybookCardinality,
  notify,
  fetchEnrolledPlaybookId,
  fetchEnrolledSequenceCount,
  fetchPlaybookForEndUser,
  uploadEmailBannerImage,
  fetchDataPreviewSequenceEmail,
  getPlaybookByCampaignId,
  fetchPlaybooksByGatedPlan,
  asyncButtonPress,
  uploadSignatureBannerImage,
  fetchSignatureMultiBannerPreview,
  setDraftSuccess,
  createInitialWelcomePlaybooks,
  fetchPlaybookForCampaignId,
  fetchPlaybooksByReportType,
  enableAllPlaybooksByReportType,
  fetchChatActivePlaybooks,
  recordPlaybookReportLoadTime,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/playbook/epic.js